/**
 * @fileoverview Ilan Detay Hook'u
 *
 * Bu modül, Emlakjet ilanlarinin detay bilgilerini getirmek icin kullanilan
 * custom hook'u icerir. ChatGPT'nin `window.openai.callTool` API'si ile
 * `emlakjet.get_listing_detail` aracini cagirarak ilan detaylarini getirir.
 *
 * Hook, kullanici jest (gesture) baglamini koruyarak `sendFollowUpMessage`
 * API'sinin calismasini saglar.
 *
 * @module useListingDetail
 * @see {@link https://www.emlakjet.com} Emlakjet
 */

import { useState, useEffect, useRef, useCallback } from "react";
import {
  extractStructuredContent,
  isCallToolAvailable,
  notifyListingDetailSmart,
  debugLog,
} from "../utils";

/**
 * @typedef {Object} ListingDetailState
 * @property {Object|null} listing - Ilan detay bilgileri
 * @property {string} kind - Yanit turu ("listing_detail")
 */

/**
 * @typedef {Object} UseListingDetailReturn
 * @property {ListingDetailState|null} listingDetail - Ilan detay state'i
 * @property {boolean} detailLoading - Detay yuklenme durumu
 * @property {number} selectedImageIndex - Secili resim indeksi
 * @property {Function} setSelectedImageIndex - Resim indeksini degistir
 * @property {Function} fetchDetail - Ilan detayini getir
 * @property {Function} clearDetail - Detay state'ini temizle
 */

/**
 * Ilan detay islemleri icin custom hook.
 *
 * Bu hook, tek bir ilanin detay bilgilerini getirmek icin kullanilir.
 * `emlakjet.get_listing_detail` MCP aracini cagirarak ilan bilgilerini alir
 * ve ChatGPT'ye bildirim gonderir.
 *
 * **Onemli Implementasyon Notu:**
 * Hook, imperatif API kullanarak kullanici jest baglamini korur.
 * Bu, `sendFollowUpMessage` API'sinin duzgun calismasini saglar.
 * Detay istegi dogrudan click handler'dan cagirilmalidir.
 *
 * **State Yonetimi:**
 * - `listingDetail`: Mevcut ilan detayi (null ise modal kapali)
 * - `detailLoading`: Yukleme spinner gosterimi icin
 * - `selectedImageIndex`: Resim galerisi icin secili indeks
 * - `notifiedListingId`: Ayni ilan icin tekrar bildirim gonderilmesini onler
 *
 * @function useListingDetail
 * @param {Function} [setCallError] - Hata mesaji set fonksiyonu
 * @returns {UseListingDetailReturn} Hook donusumu
 *
 * @example
 * const {
 *   listingDetail,
 *   detailLoading,
 *   selectedImageIndex,
 *   setSelectedImageIndex,
 *   fetchDetail,
 *   clearDetail,
 * } = useListingDetail(setCallError);
 *
 * // Ilan detayini getir (click handler icinde)
 * const handleListingClick = (listingId) => {
 *   fetchDetail(listingId);
 * };
 *
 * // Modali kapat
 * const handleCloseModal = () => {
 *   clearDetail();
 * };
 */
export function useListingDetail(setCallError) {
  const [listingDetail, setListingDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  // Hangi ilan icin ChatGPT'ye bildirim gonderdigimizi takip et
  const notifiedListingId = useRef(null);

  // Ilan degistiginde resim indeksini sifirla
  useEffect(() => {
    setSelectedImageIndex(0);
  }, [listingDetail?.listing?.id]);

  /**
   * Ilan detayini getirir.
   *
   * **ONEMLI:** Bu fonksiyon dogrudan click handler'dan cagrilmalidir,
   * useEffect veya diger asenkron baglamlardan degil. Bu, kullanici
   * jest baglamini koruyarak `sendFollowUpMessage` API'sinin calismasini saglar.
   *
   * @async
   * @function fetchDetail
   * @param {string} listingId - Getirilecek ilanin ID'si
   * @returns {Promise<void>}
   *
   * @example
   * <button onClick={() => fetchDetail("12345")}>
   *   Ilan Detayi
   * </button>
   */
  const fetchDetail = useCallback(
    async (listingId) => {
      debugLog(`fetchDetail: ${listingId}`);

      if (!listingId) {
        debugLog("fetchDetail: no listingId");
        setListingDetail(null);
        return;
      }
      if (!isCallToolAvailable()) {
        debugLog("fetchDetail: callTool NOT available");
        return;
      }

      setDetailLoading(true);
      setCallError?.(null);
      try {
        debugLog("fetchDetail: calling callTool...");
        const resp = await window.openai.callTool(
          "emlakjet.get_listing_detail",
          {
            id: listingId,
          }
        );
        debugLog("fetchDetail: callTool done");

        // Normalize: callTool returns { structuredContent, _meta }
        const out = extractStructuredContent(resp);
        debugLog(`fetchDetail: out.kind=${out?.kind}, has listing=${!!out?.listing}`);

        // Gate on listing, not kind - tool is specific so if listing exists, use it
        if (!out?.listing) {
          debugLog("fetchDetail: no listing in response");
          return;
        }

        // Set detail state (with fallback kind if missing)
        setListingDetail({
          kind: out.kind ?? "listing_detail",
          ...out,
        });

        // Notify ChatGPT about the listing (optional - don't block if fails)
        // Uses smart detection: land listings get arsa-specific commentary
        const listing = out.listing;
        if (listing && notifiedListingId.current !== listing.id) {
          notifiedListingId.current = listing.id;
          debugLog("fetchDetail: calling notify (smart)...");
          // Fire and forget - don't let notify failure break the flow
          notifyListingDetailSmart(listing).catch((err) => {
            debugLog(`fetchDetail: notify failed (non-blocking): ${err?.message || err}`);
          });
        } else if (listing) {
          debugLog(`fetchDetail: skip notify (same id: ${notifiedListingId.current})`);
        }
      } catch (e) {
        debugLog(`fetchDetail: ERROR ${e?.message || e}`);
        setCallError?.(e instanceof Error ? e.message : "İlan detayı alınamadı");
      } finally {
        setDetailLoading(false);
      }
    },
    [setCallError]
  );

  /**
   * Mevcut ilan detayini temizler ve modali kapatir.
   *
   * @function clearDetail
   * @returns {void}
   */
  const clearDetail = useCallback(() => {
    setListingDetail(null);
  }, []);

  return {
    listingDetail,
    detailLoading,
    selectedImageIndex,
    setSelectedImageIndex,
    fetchDetail,
    clearDetail,
  };
}

export default useListingDetail;
